"use client";

import React from "react";
import { useCreatePageStore } from "@/stores/createPageStore";
import { Text } from "@/components/ui/text";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import {
  formatTimeRemaining,
  getLoadingMessage,
} from "../createAgentChat.utils";
import "../agent-activity.css";

/** Preview loading lives on the canvas; legacy chat layouts can opt into chat. */
export default function ChatGenerationProgress({
  placement = "chat",
}: {
  placement?: "chat" | "canvas";
}) {
  const loading = useCreatePageStore(
    state => state.generation.progress.loading
  );
  const isModifying = useCreatePageStore(
    state => state.ui.modification.isModifying
  );
  const progress = useCreatePageStore(
    state => state.generation.progress.progress
  );
  const generationPhase = useCreatePageStore(
    state => state.generation.progress.phase
  );
  const estimatedTimeRemaining = useCreatePageStore(
    state => state.generation.progress.estimatedTimeRemaining
  );

  if (!(loading || isModifying)) {
    return null;
  }

  const loadingMessage = getLoadingMessage({
    isModifying,
    estimatedTimeRemaining,
    generationPhase,
  });
  const side = generationPhase === "back" ? "back" : "front";
  const generationTitle = `${isModifying ? "Updating" : "Generating"} ${side} preview`;
  const displayProgress = Math.min(99, Math.max(0, Math.round(progress || 0)));

  return (
    <div
      data-slot={`${placement}-generation-progress`}
      className={cn(
        placement === "canvas"
          ? "w-full max-w-xs rounded-xl border border-border bg-card/95 p-4 shadow-sm backdrop-blur-sm"
          : "py-2 lg:hidden"
      )}
    >
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Text
            as="span"
            size="default"
            weight="medium"
            role="status"
            aria-live="polite"
            className="agent-activity-status min-w-0 flex-1 text-muted-foreground"
          >
            {generationTitle}
          </Text>
        </div>
        <Text as="span" variant="muted" size="sm" className="block leading-5">
          {loadingMessage}
        </Text>
        <Progress
          value={displayProgress}
          aria-label={generationTitle}
          aria-valuenow={displayProgress}
          className="h-1.5 bg-muted [&>[data-slot=progress-indicator]]:bg-brand-logo"
        />
        <div className="flex items-center justify-between gap-3 text-xs tabular-nums text-muted-foreground">
          <span>
            {estimatedTimeRemaining !== null &&
            estimatedTimeRemaining !== undefined &&
            estimatedTimeRemaining > 0
              ? `~${formatTimeRemaining(estimatedTimeRemaining)} left`
              : estimatedTimeRemaining !== null &&
                  estimatedTimeRemaining !== undefined &&
                  estimatedTimeRemaining <= 0
                ? "Finalizing"
                : "Usually 30-60 seconds"}
          </span>
          <span>{displayProgress}%</span>
        </div>
      </div>
    </div>
  );
}
